/**
 * sGTM Debug Preview
 *
 * Reads `sgtm_preview=1&sgtm_preview_token=<token>` from the URL,
 * validates, stores in module-level state, and exposes helpers so every
 * dataLayer push includes the preview fields for the router sGTM to
 * forward as `X-Gtm-Server-Preview`.
 *
 * In-memory only — lost on full page reload (correct: user re-appends
 * params for a new debug session).
 */

// ---------------------------------------------------------------------------
// Module-level state (same pattern as gtmLoader.ts's `let gtmLoaded`)
// ---------------------------------------------------------------------------

interface SgtmPreviewState {
  enabled: boolean;
  token: string;
  expiresAt: number; // Unix ms
}

import { log, warn } from './logger';

let previewState: SgtmPreviewState = { enabled: false, token: '', expiresAt: 0 };

const DEFAULT_TTL_MINUTES = 15;
const MAX_TOKEN_LENGTH = 2000;
const TOKEN_PATTERN = /^[A-Za-z0-9+/=\-_.]+$/;

// ---------------------------------------------------------------------------
// capturePreviewParams — parse, validate, store in module state
// ---------------------------------------------------------------------------

/**
 * Read sGTM preview params from the current URL, validate them, and store
 * in module state. Params are left in the address bar for easier debugging
 * and URL sharing.
 *
 * Safe to call multiple times — returns immediately when no preview params
 * are present.
 */
export function capturePreviewParams(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);

  const flag = urlParams.get('sgtm_preview');
  const token = urlParams.get('sgtm_preview_token');
  const ttlParam = urlParams.get('sgtm_preview_ttl_minutes');

  // Nothing to do if no preview params in URL
  if (!flag && !token && !ttlParam) {
    return;
  }

  // Validate
  if (flag !== '1' || !token) {
    warn('[sGTM Preview] Invalid or missing params — preview not activated');
    return;
  }

  if (token.length > MAX_TOKEN_LENGTH) {
    warn('[sGTM Preview] Token exceeds max length — preview not activated');
    return;
  }

  if (!TOKEN_PATTERN.test(token)) {
    warn('[sGTM Preview] Token contains invalid characters — preview not activated');
    return;
  }

  const ttlMinutes =
    ttlParam && Number.isFinite(Number(ttlParam)) && Number(ttlParam) > 0
      ? Number(ttlParam)
      : DEFAULT_TTL_MINUTES;

  previewState = {
    enabled: true,
    token,
    expiresAt: Date.now() + ttlMinutes * 60 * 1000
  };

  log('[sGTM Preview] Activated', { ttlMinutes });
}

// ---------------------------------------------------------------------------
// pushPreviewContext — seed dataLayer before GTM loads
// ---------------------------------------------------------------------------

/**
 * Push a data-only message (no `event` property) to `window.dataLayer` so
 * Web GTM can map the fields into GA4 event params.
 *
 * Only pushes when preview is active.
 */
export function pushPreviewContext(): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (!previewState.enabled || Date.now() >= previewState.expiresAt) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    tablz_sgtm_preview: '1',
    tablz_sgtm_preview_token: previewState.token
  } as any);

  log('[sGTM Preview] Initial debug context pushed');
}

// ---------------------------------------------------------------------------
// getPreviewFields — called by pushToDataLayer on every event
// ---------------------------------------------------------------------------

/**
 * Returns preview fields to spread into every dataLayer event.
 * Checks TTL lazily — when expired, resets state and returns inactive fields.
 *
 * Internal: consumed only by `dataLayer.ts`, not re-exported from barrel.
 */
export function getPreviewFields(): { tablz_sgtm_preview: '1' | '0'; tablz_sgtm_preview_token: string } {
  if (previewState.enabled && Date.now() >= previewState.expiresAt) {
    previewState = { enabled: false, token: '', expiresAt: 0 };

    log('[sGTM Preview] Session expired');
  }

  if (previewState.enabled) {
    return { tablz_sgtm_preview: '1', tablz_sgtm_preview_token: previewState.token };
  }

  return { tablz_sgtm_preview: '0', tablz_sgtm_preview_token: '' };
}

// ---------------------------------------------------------------------------
// isPreviewActive — convenience boolean check
// ---------------------------------------------------------------------------

export function isPreviewActive(): boolean {
  return previewState.enabled && Date.now() < previewState.expiresAt;
}
